import endpoints from "../../../../utils/endpoints";
import type { FlowStep } from "../types";
import { FlowStepError } from "../engine/errors";
import { garaazTransitionPath, requireEnquiry, verifyEq } from "./common";

export const STEP_CREATE_ENQUIRY = "create_enquiry";
export const STEP_ACK_ENQUIRY = "ack_enquiry";
export const STEP_PART_CONVERSION_START = "part_conversion_start";
export const STEP_PART_CONVERSION_END = "part_conversion_end";

const isBakedEnquiry = (order: any) =>
  Array.isArray(order?.orderItems) && order.orderItems.length > 0;

export const createEnquiryStep: FlowStep = {
  id: STEP_CREATE_ENQUIRY,
  name: "Place enquiry",
  targetLabel: "Enquiry placed",
  description:
    "Customer places an enquiry (POST /v1/orders without rootOrderId). A baked enquiry (at least one part) is auto-ACK'd and part conversion is stamped start+end, which sends part confirmation to the IAM supplier.",
  actor: "CUSTOMER",
  kind: "api",
  estimateMs: [1500, 4000],
  execute: async (rt) => {
    const { config, orcaUser } = rt.ctx;
    if (!config.customerId) throw new FlowStepError("No test customer selected.");
    if (!config.parts.length) throw new FlowStepError("Add at least one part to the configuration.");
    if (!config.shipmentAddress) {
      throw new FlowStepError("No shipment address configured for the customer.");
    }

    const payload = {
      customerId: config.customerId,
      initiatorId: orcaUser._id,
      customerRemarks: config.remarks || undefined,
      shipmentAddress: config.shipmentAddress,
      billingDetails: config.billingDetails || undefined,
      orderDate: new Date().toISOString(),
      orderItems: config.parts.map((p) => ({
        partId: p.partId,
        orderedQuantity: p.quantity,
      })),
      orderMedia: config.orderMediaIds.length ? config.orderMediaIds : undefined,
      carMaker: config.carMaker || undefined,
      isTestOrder: true,
    };

    const res = await rt.api<any>({
      label: "Place enquiry",
      method: "POST",
      path: endpoints.placeGaraazOrder,
      body: payload,
      actor: "CUSTOMER",
    });
    const order = res.data?.order ?? res.data;
    if (!order?._id || !order?.orderNo) {
      throw new FlowStepError("Enquiry response did not include an order id/number.", {
        apiCall: res.record,
      });
    }
    if (order.isTestOrder !== true) {
      throw new FlowStepError(
        "Safety stop: the created order is NOT flagged isTestOrder. Aborting so no production flow continues.",
        { expected: "isTestOrder = true", actual: `isTestOrder = ${order.isTestOrder}`, apiCall: res.record }
      );
    }
    rt.ctx.enquiry = { orderId: String(order._id), orderNo: order.orderNo };
    const baked = isBakedEnquiry(order) || Boolean(rt.ctx.config.parts.length);
    if (baked && (order.orderStatus === "ACK" || order.metaInfo?.partConversionEndTime)) {
      rt.log(
        `Baked enquiry: backend auto-ACK'd and completed part conversion (part confirmation is sent asynchronously)`,
        "info"
      );
    }
    rt.log(
      `Enquiry ${order.orderNo} created (${order.orderStatus}) enquiryNo=${order.orderNo} _id=${order._id}`,
      "success"
    );
  },
  verify: (s) =>
    verifyEq(
      "enquiry.orderStatus",
      ["NEW", "ACK"],
      s.enquiry?.orderStatus
    ),
};

export const ackEnquiryStep: FlowStep = {
  id: STEP_ACK_ENQUIRY,
  name: "ACK enquiry",
  targetLabel: "Enquiry ACK",
  description:
    "OrCA acknowledges the enquiry (POST /v1/orders/:id/garaaz_transition?action=ACK). Baked enquiries are already ACK'd on create — this step records that as completed.",
  actor: "ORCA",
  kind: "api",
  estimateMs: [500, 2500],
  execute: async (rt) => {
    const enquiry = requireEnquiry(rt.ctx);
    const status = rt.ctx.snapshot?.enquiry?.orderStatus;
    if (status && status !== "NEW") {
      rt.log(`Enquiry already ${status} (backend auto-ACK on baked create)`, "info");
      return;
    }
    await rt.api({
      label: "ACK enquiry",
      method: "POST",
      path: garaazTransitionPath(enquiry.orderId, "ACK"),
      body: {},
    });
  },
  verify: (s) =>
    verifyEq("enquiry.orderStatus", ["ACK", "ALLOCATED", "BLOCKED"], s.enquiry?.orderStatus),
};

export const partConversionStartStep: FlowStep = {
  id: STEP_PART_CONVERSION_START,
  name: "Start part conversion",
  targetLabel: "Part conversion started",
  description:
    "OrCA marks part conversion start (PATCH meta-info partConversionStartTime). Baked enquiries already have this stamped — recorded as completed.",
  actor: "ORCA",
  kind: "api",
  estimateMs: [500, 2000],
  execute: async (rt) => {
    const enquiry = requireEnquiry(rt.ctx);
    if (rt.ctx.snapshot?.enquiry?.partConversion !== "NOT_STARTED") {
      rt.log("Part conversion already started (backend stamped it on baked create)", "info");
      return;
    }
    await rt.api({
      label: "Start part conversion",
      method: "PATCH",
      path: endpoints.updateOrderMetaInfo(enquiry.orderId),
      body: { partConversionStartTime: new Date().toISOString() },
    });
  },
  verify: (s) =>
    verifyEq("enquiry.partConversion", ["IN_PROGRESS", "COMPLETED"], s.enquiry?.partConversion),
};

export const partConversionEndStep: FlowStep = {
  id: STEP_PART_CONVERSION_END,
  name: "Complete part conversion",
  targetLabel: "Part conversion completed",
  description:
    "OrCA completes part conversion (PATCH meta-info partConversionEndTime), which triggers IAM part confirmation. Baked enquiries already have this stamped — recorded as completed.",
  actor: "ORCA",
  kind: "api",
  estimateMs: [500, 2000],
  execute: async (rt) => {
    const enquiry = requireEnquiry(rt.ctx);
    if (rt.ctx.snapshot?.enquiry?.partConversion === "COMPLETED") {
      rt.log("Part conversion already completed (backend stamped it on baked create)", "info");
      rt.markEventProcessed();
      return;
    }
    await rt.api({
      label: "Complete part conversion",
      method: "PATCH",
      path: endpoints.updateOrderMetaInfo(enquiry.orderId),
      body: { partConversionEndTime: new Date().toISOString() },
    });
    rt.markEventProcessed();
  },
  verify: (s) => verifyEq("enquiry.partConversion", "COMPLETED", s.enquiry?.partConversion),
};
